import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserService } from '../services/security/user.service';
import { AuthService } from '../services/security/auth.service';
import { SubscriptionService } from '../services/subscription.service';

export const subscriptionGuard: CanActivateFn = (): Observable<boolean> => {
  const subscriptionService = inject(SubscriptionService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const userService = inject(UserService);

  // If subscriptions are disabled app wide, allow access
  if (!userService.subscriptionsEnabled) {
    return of(true);
  }

  // First check if user sub is processing
  if (authService.subscriptionStatus === 'processing') {
    router.navigate(['/after-payment']);
    return of(false);
  }

  // First check if user needs organization onboarding
  const needsOrganizationOnboarding = authService.currentUser?.needs_organization_onboarding;

  if (needsOrganizationOnboarding) {
    // User needs organization onboarding - redirect to organization onboarding
    router.navigate(['/organization/onboarding']);
    return of(false);
  }

  // Check if organization subscription is expired
  const orgSubscriptionExpired = authService.hasExpiredOrganizationSubscription();

  if (orgSubscriptionExpired) {
    // Organization subscription expired - redirect to onboarding with special message
    router.navigate(['/onboarding']);
    return of(false);
  }

  // Analysts only view platform statistics and feedback, so having no subscription
  // must not trap them in onboarding; they browse like an expired user.
  const isAnalyst = authService.currentUser?.role === 'analyst';

  // Then check subscription status
  const subscriptionStatus = authService.subscriptionStatus;

  if (subscriptionStatus) {
    // We have subscription status from token - use it for immediate routing decision
    if (subscriptionStatus === 'none') {
      if (isAnalyst) return of(true);
      router.navigate(['/onboarding']);
      return of(false);
    }

    if (subscriptionStatus === 'expired') {
      // Allow expired users to access cabinet with restricted functionality
      // They can view/open projects but cannot download/edit
      return of(true);
    }

    // Status is 'active' - allow full access
    return of(true);
  }

  // Fallback: If no subscription status in token, make HTTP call to get current status
  return subscriptionService.getSubscriptionStatus().pipe(
    map((response) => {
      const status = response.status;

      if (status === 'none') {
        if (isAnalyst) return true;
        router.navigate(['/onboarding']);
        return false;
      }

      if (status === 'expired') {
        // Allow expired users to access cabinet with restricted functionality
        return true;
      }

      // Status is 'active' - allow full access
      return true;
    }),
    catchError(() => {
      // If there's an error getting subscription, redirect to onboarding
      router.navigate(['/onboarding']);
      return of(false);
    })
  );
};
